import React from 'react';
import { MapPin, Calculator, Star, Moon, Sparkles, Heart, History, User, BookOpen, FileText, Mail, Shield, ArrowUp } from 'lucide-react';
interface SitemapSection {
    title: string;
    description: string;
    links: {
        name: string;
        path: string;
        description: string;
        icon: React.ReactNode;
    }[];
}

const SITEMAP_SECTIONS: SitemapSection[] = [
    {
        title: "Interactive Tools",
        description: "Our core calculators and interactive features for age calculation, zodiac exploration, and personal insights.",
        links: [
            {
                name: "Age Calculator",
                path: "/",
                description: "Calculate your exact age in years, months, days, hours, minutes, and seconds with real-time updates.",
                icon: <Calculator size={20} />
            },
            {
                name: "Western Zodiac",
                path: "/zodiac",
                description: "Discover your Sun sign, Moon sign, and Rising sign. Explore compatibility and weekly forecasts.",
                icon: <Star size={20} />
            },
            {
                name: "Chinese Zodiac",
                path: "/chinese-zodiage",
                description: "Find your Chinese zodiac animal based on birth year. Learn about elements and compatibility.",
                icon: <Moon size={20} />
            },
            {
                name: "Tarot Reader",
                path: "/tarot",
                description: "Draw daily tarot cards from the Major Arcana. Get AI-powered reflections and interpretations.",
                icon: <Sparkles size={20} />
            },
            {
                name: "Personality Profile",
                path: "/personality",
                description: "Generate a personalized personality profile combining Western and Chinese zodiac insights.",
                icon: <User size={20} />
            },
            {
                name: "Life Progress",
                path: "/progress",
                description: "Visualize your life as a progress bar based on expected lifespan. Includes solar cycle view.",
                icon: <Heart size={20} />
            },
            {
                name: "Life Timeline",
                path: "/timeline",
                description: "Explore major life milestones and astrological cycles like Saturn Return and Chiron Return.",
                icon: <History size={20} />
            }
        ]
    },
    {
        title: "Learning Resources",
        description: "Educational content to help you understand astrology, tarot, and symbolic systems.",
        links: [
            {
                name: "Learning Center",
                path: "/resources",
                description: "Comprehensive guides and tutorials on astrology, tarot, zodiac systems, and more.",
                icon: <BookOpen size={20} />
            },
            {
                name: "Glossary",
                path: "/glossary",
                description: "Complete dictionary of astrology, tarot, and zodiac terminology with detailed definitions.",
                icon: <FileText size={20} />
            }
        ]
    },
    {
        title: "About & Legal",
        description: "Information about ZodiAge, our policies, and how to contact us.",
        links: [
            {
                name: "About ZodiAge",
                path: "/about",
                description: "Learn about our mission, the team behind ZodiAge, and what we offer.",
                icon: <User size={20} />
            },
            {
                name: "Contact Us",
                path: "/contact",
                description: "Get in touch with questions, feedback, bug reports, or suggestions.",
                icon: <Mail size={20} />
            },
            {
                name: "Privacy Policy",
                path: "/privacy",
                description: "Understand how we handle your data and protect your privacy.",
                icon: <Shield size={20} />
            },
            {
                name: "Terms of Service",
                path: "/terms",
                description: "The terms and conditions governing your use of ZodiAge.",
                icon: <FileText size={20} />
            },
            {
                name: "Site Map",
                path: "/sitemap",
                description: "This page - a complete overview of all pages and features on ZodiAge.",
                icon: <MapPin size={20} />
            }
        ]
    }
];

const SitemapPage: React.FC = () => {
    return (
        <div className="space-y-12 animate-in fade-in duration-500 pb-12">
            {/* Header */}
            <article className="max-w-4xl mx-auto">
                <header className="text-center mb-8">
                    <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
                        Site Map – Navigate ZodiAge
                    </h1>
                    <p className="text-gray-300 text-lg max-w-2xl mx-auto leading-relaxed">
                        A complete overview of all pages, tools, and resources available on ZodiAge. 
                        Use this page to find exactly what you're looking for or discover new features.
                    </p>
                </header>

                {/* Quick Stats */}
                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50 mb-8">
                    <h2 className="text-xl font-bold text-white mb-4">What You'll Find on ZodiAge</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div className="bg-black/20 p-4 rounded-xl">
                            <div className="text-3xl font-display font-bold text-primary">7+</div>
                            <div className="text-gray-400 text-sm">Interactive Tools</div>
                        </div>
                        <div className="bg-black/20 p-4 rounded-xl">
                            <div className="text-3xl font-display font-bold text-secondary">40+</div>
                            <div className="text-gray-400 text-sm">Glossary Terms</div>
                        </div>
                        <div className="bg-black/20 p-4 rounded-xl">
                            <div className="text-3xl font-display font-bold text-green-400">12</div>
                            <div className="text-gray-400 text-sm">Zodiac Signs</div>
                        </div>
                        <div className="bg-black/20 p-4 rounded-xl">
                            <div className="text-3xl font-display font-bold text-purple-400">22</div>
                            <div className="text-gray-400 text-sm">Tarot Cards</div>
                        </div>
                    </div>
                </section>
            </article>

            {/* Sitemap Sections */}
            <div className="max-w-4xl mx-auto space-y-8">
                {SITEMAP_SECTIONS.map((section, index) => (
                    <section key={index} className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50">
                        <h2 className="text-2xl font-bold text-white mb-2">{section.title}</h2>
                        <p className="text-gray-400 text-sm mb-6">{section.description}</p>
                        
                        <div className="space-y-3">
                            {section.links.map((link, linkIndex) => (
                                <a
                                    key={linkIndex}
                                    href={link.path}
                                    className="flex items-start gap-4 p-4 bg-black/20 rounded-xl hover:bg-black/30 transition-colors group"
                                >
                                    <div className="bg-primary/20 p-2 rounded-lg text-primary group-hover:bg-primary group-hover:text-black transition-colors">
                                        {link.icon}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-white group-hover:text-primary transition-colors">
                                            {link.name}
                                        </h3>
                                        <p className="text-gray-400 text-sm mt-1">{link.description}</p>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </section>
                ))}
            </div>

            {/* Additional Information */}
            <article className="max-w-4xl mx-auto space-y-8">
                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50">
                    <h2 className="text-xl font-bold text-white mb-4">About This Site</h2>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                        ZodiAge is a free, ad-supported web application combining age calculation tools with educational 
                        content about Western astrology, Chinese zodiac, tarot symbolism, and life milestone psychology. 
                        All features are available without registration or payment.
                    </p>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                        The site is built using modern web technologies (React, TypeScript, Tailwind CSS) and runs 
                        primarily in your browser. Your personal information (birth date, preferences) is stored locally 
                        on your device, not on remote servers.
                    </p>
                    <p className="text-gray-300 text-sm leading-relaxed">
                        All astrological and tarot content is provided for entertainment and educational purposes only. 
                        These symbolic systems are cultural traditions, not scientifically validated prediction methods. 
                        Please consult qualified professionals for important life decisions.
                    </p>
                </section>

                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50">
                    <h2 className="text-xl font-bold text-white mb-4">Technical Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                            <h4 className="font-bold text-white mb-2">Browser Compatibility</h4>
                            <p className="text-gray-400">
                                ZodiAge works on all modern browsers including Chrome, Firefox, Safari, and Edge. 
                                JavaScript must be enabled for full functionality.
                            </p>
                        </div>
                        <div>
                            <h4 className="font-bold text-white mb-2">Mobile Support</h4>
                            <p className="text-gray-400">
                                The site is fully responsive and works on smartphones and tablets. All features 
                                are touch-friendly and optimized for smaller screens.
                            </p>
                        </div>
                        <div>
                            <h4 className="font-bold text-white mb-2">Data Storage</h4>
                            <p className="text-gray-400">
                                Personal data is stored in your browser's localStorage under keys "zodiage_user" 
                                and "zodiage_settings_v1". Clear your browser data to remove this information.
                            </p>
                        </div>
                        <div>
                            <h4 className="font-bold text-white mb-2">AI Features</h4>
                            <p className="text-gray-400">
                                Some features use Google Gemini AI to generate personalized content. These calls 
                                do not include personally identifiable information like full birth dates or names.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="bg-gradient-to-br from-primary/10 to-secondary/10 p-6 rounded-2xl border border-primary/30">
                    <h2 className="text-xl font-bold text-white mb-4">Need Help?</h2>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                        Can't find what you're looking for? Have questions about how a feature works? 
                        We're here to help.
                    </p>
                    <div className="flex flex-wrap gap-4">
                        <a 
                            href="/contact" 
                            className="inline-flex items-center gap-2 bg-primary text-black font-bold px-6 py-3 rounded-xl hover:bg-primary/80 transition-colors"
                        >
                            <Mail size={18} />
                            Contact Us
                        </a>
                        <a 
                            href="/resources" 
                            className="inline-flex items-center gap-2 bg-cosmic-700/50 text-white font-bold px-6 py-3 rounded-xl hover:bg-cosmic-600/50 transition-colors"
                        >
                            <BookOpen size={18} />
                            Learning Center
                        </a>
                    </div>
                </section>
            </article>

            {/* Schema-friendly structured content */}
            <article className="max-w-4xl mx-auto">
                <section className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50">
                    <h2 className="text-xl font-bold text-white mb-4">Quick Links by Category</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                        <div>
                            <h3 className="font-bold text-primary mb-2">Calculators</h3>
                            <ul className="space-y-1 text-gray-300">
                                <li><a href="/" className="hover:text-primary transition-colors">• Age Calculator</a></li>
                                <li><a href="/zodiac" className="hover:text-primary transition-colors">• Zodiac Sign Calculator</a></li>
                                <li><a href="/chinese-zodiage" className="hover:text-primary transition-colors">• Chinese Zodiac Calculator</a></li>
                                <li><a href="/progress" className="hover:text-primary transition-colors">• Life Progress Calculator</a></li>
                            </ul>
                        </div>
                        <div>
                            <h3 className="font-bold text-secondary mb-2">Readings & Profiles</h3>
                            <ul className="space-y-1 text-gray-300">
                                <li><a href="/tarot" className="hover:text-secondary transition-colors">• Daily Tarot Reading</a></li>
                                <li><a href="/personality" className="hover:text-secondary transition-colors">• Personality Profile</a></li>
                                <li><a href="/timeline" className="hover:text-secondary transition-colors">• Life Timeline</a></li>
                                <li><a href="/zodiac" className="hover:text-secondary transition-colors">• Compatibility Check</a></li>
                            </ul>
                        </div>
                        <div>
                            <h3 className="font-bold text-green-400 mb-2">Learning & Info</h3>
                            <ul className="space-y-1 text-gray-300">
                                <li><a href="/resources" className="hover:text-green-400 transition-colors">• Astrology Guides</a></li>
                                <li><a href="/glossary" className="hover:text-green-400 transition-colors">• Term Glossary</a></li>
                                <li><a href="/about" className="hover:text-green-400 transition-colors">• About Us</a></li>
                                <li><a href="/privacy" className="hover:text-green-400 transition-colors">• Privacy Policy</a></li>
                            </ul>
                        </div>
                    </div>
                </section>
            </article>

            {/* Back to Top Button */}
            <div className="flex justify-center mt-8">
                <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white px-6 py-3 rounded-full shadow-lg shadow-purple-600/30 transition-all hover:scale-105 hover:shadow-purple-600/50"
                >
                    <ArrowUp size={18} />
                    <span>Back to Top</span>
                </button>
            </div>
        </div>
    );
};

export default SitemapPage;
